import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Icon, InputLabel } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDSelect from "components/MDSelect";
import { MDTextFieldCalendar } from "components/MDTextFiedCalendar";
import { useMaterialUIController } from "context";
import {
    FILTROS, GRUPOS_RELATORIOS, PERIODOS, PERIODO_QUALQUER, dataBr, intervaloDoPeriodo, opcoesDe, selecaoInicial, validarSelecao,
} from "./relatoriosProntos";
import { ESCURO } from "assets/coresTema";

/**
 * Filtros de um relatório pronto (ou customizado): só os que fazem sentido para ele, com período em um clique.
 * "Ver na tela" e "Baixar planilha" chamam o pai; erro (filtro obrigatório, SQL) aparece aqui mesmo, sem fechar.
 */
export default function RelatorioProntoDialog({ definicao, selecao, listas, onClose, onVerNaTela, onBaixar, estilo, onAparencia }) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const [sel, setSel] = useState(null);
    const [erro, setErro] = useState(null);
    const [acao, setAcao] = useState(null); // "tela" | "planilha" enquanto gera

    useEffect(() => {
        if (definicao) {
            setSel(selecao || selecaoInicial(definicao));
            setErro(null);
            setAcao(null);
        }
    }, [definicao, selecao]);

    if (!definicao || !sel) return null;

    const grupo = GRUPOS_RELATORIOS.find(g => g.id === definicao.grupo) || GRUPOS_RELATORIOS[0];
    const cor = darkMode ? grupo.corEscura : grupo.cor;
    const textoPrincipal = darkMode ? "#FFFFFF" : "#344767";
    const textoSecundario = darkMode ? "rgba(255, 255, 255, 0.8)" : "#6c757d";
    const labelStyle = { pb: 0.5 }; // aparência do rótulo vem do tema
    const obrigatorios = definicao.obrigatorios || [];
    const temPeriodo = definicao.filtros.includes("periodo");
    const outrosFiltros = definicao.filtros.filter(f => f !== "periodo");
    const periodos = definicao.periodoPadrao === "todos" ? [PERIODO_QUALQUER, ...PERIODOS] : PERIODOS;
    const intervalo = intervaloDoPeriodo(sel.periodo, sel.inicio, sel.fim);
    const algumOpcional = outrosFiltros.some(f => !obrigatorios.includes(f));

    const alterar = (campo, valor) => {
        setSel(atual => ({ ...atual, [campo]: valor }));
        setErro(null);
    };

    const executar = async (tipo) => {
        const problema = validarSelecao(definicao, sel);
        if (problema) {
            setErro(problema);
            return;
        }
        setAcao(tipo);
        setErro(null);
        try {
            await (tipo === "tela" ? onVerNaTela : onBaixar)(definicao, sel);
        } catch (e) {
            setErro(e?.message || "Não foi possível gerar o relatório.");
        } finally {
            setAcao(null);
        }
    };

    const chipPeriodo = (p) => {
        const ativo = sel.periodo === p.id;
        return (
            <Chip
                key={p.id}
                label={p.label}
                size="small"
                clickable
                onClick={() => alterar("periodo", p.id)}
                icon={ativo ? <Icon sx={{ fontSize: "16px !important", color: "#FFFFFF !important" }}>check</Icon> : undefined}
                sx={{
                    fontWeight: ativo ? 700 : 500,
                    color: ativo ? "#FFFFFF" : textoPrincipal,
                    backgroundColor: ativo ? "#1A73E8" : (darkMode ? "rgba(255,255,255,0.08)" : "#f0f2f5"),
                    border: ativo ? "1px solid #1A73E8" : (darkMode ? `1px solid ${ESCURO.borda}` : "1px solid #dfe3e8"),
                    "&:hover": { backgroundColor: ativo ? "#1662C4" : (darkMode ? "rgba(255,255,255,0.16)" : "#e4e8ee") },
                }}
            />
        );
    };

    return (
        <Dialog
            open
            onClose={acao ? undefined : onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    background: darkMode ? `linear-gradient(145deg, ${ESCURO.campo} 0%, ${ESCURO.painel} 100%)` : "#ffffff",
                    color: darkMode ? "#FFFFFF" : "inherit",
                    borderRadius: "12px",
                    border: darkMode ? `1px solid ${ESCURO.borda}` : "1px solid #e0e0e0",
                },
            }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <MDBox display="flex" alignItems="center" gap={1.5}>
                    <MDBox
                        display="flex" alignItems="center" justifyContent="center" borderRadius="10px"
                        sx={{ width: 40, height: 40, flexShrink: 0, backgroundColor: darkMode ? "rgba(255,255,255,0.08)" : "#f0f2f5", color: cor }}
                    >
                        <Icon>{definicao.icone}</Icon>
                    </MDBox>
                    <MDBox sx={{ minWidth: 0 }}>
                        <MDTypography variant="h6" fontWeight="bold" sx={{ color: textoPrincipal, lineHeight: 1.3 }}>
                            {definicao.titulo}
                        </MDTypography>
                        <MDTypography variant="caption" display="block" sx={{ color: textoSecundario }}>
                            {definicao.descricao}
                        </MDTypography>
                    </MDBox>
                </MDBox>
            </DialogTitle>

            <DialogContent sx={{ pt: "8px !important" }}>
                {temPeriodo && (
                    <MDBox mb={2.5}>
                        <InputLabel sx={labelStyle}>
                            {definicao.rotuloPeriodo || "Período"}{obrigatorios.includes("periodo") ? " *" : ""}
                        </InputLabel>
                        <MDBox display="flex" flexWrap="wrap" gap={0.75}>
                            {periodos.map(chipPeriodo)}
                        </MDBox>
                        {sel.periodo === "personalizado" ? (
                            <Grid container spacing={1.5} mt={0.25}>
                                <Grid item xs={6}>
                                    <MDTextFieldCalendar
                                        type="date"
                                        darkMode={darkMode}
                                        fullWidth
                                        value={sel.inicio}
                                        onChange={(e) => alterar("inicio", e.target.value)}
                                        inputProps={{ "aria-label": "Data inicial" }}
                                    />
                                    <MDTypography variant="caption" sx={{ color: textoSecundario }}>Data inicial</MDTypography>
                                </Grid>
                                <Grid item xs={6}>
                                    <MDTextFieldCalendar
                                        type="date"
                                        darkMode={darkMode}
                                        fullWidth
                                        value={sel.fim}
                                        onChange={(e) => alterar("fim", e.target.value)}
                                        inputProps={{ "aria-label": "Data final" }}
                                    />
                                    <MDTypography variant="caption" sx={{ color: textoSecundario }}>Data final</MDTypography>
                                </Grid>
                            </Grid>
                        ) : (
                            <MDTypography variant="caption" display="block" mt={0.75} sx={{ color: textoSecundario }}>
                                {intervalo.inicio ? `De ${dataBr(intervalo.inicio)} a ${dataBr(intervalo.fim)}` : "Sem limite de data"}
                            </MDTypography>
                        )}
                    </MDBox>
                )}

                {outrosFiltros.length > 0 && (
                    <Grid container spacing={1.5}>
                        {outrosFiltros.map(chave => {
                            const filtro = FILTROS[chave];
                            return (
                                <Grid item xs={12} sm={outrosFiltros.length > 1 ? 6 : 12} key={chave}>
                                    <InputLabel sx={labelStyle}>{filtro.label}{obrigatorios.includes(chave) ? " *" : ""}</InputLabel>
                                    <MDSelect
                                        isMulti
                                        name={chave}
                                        value={sel[chave]}
                                        options={opcoesDe(chave, listas[filtro.lista])}
                                        onChange={(opts) => alterar(chave, opts || [])}
                                        placeholder={obrigatorios.includes(chave) ? "Escolha ao menos um" : filtro.placeholder}
                                    />
                                </Grid>
                            );
                        })}
                    </Grid>
                )}

                {(algumOpcional || definicao.exigeUmDe) && (
                    <MDTypography variant="caption" display="block" mt={1} sx={{ color: textoSecundario }}>
                        {definicao.exigeUmDe
                            ? "Escolha ao menos um cliente, carteira ou operador. Os demais filtros em branco = todos."
                            : "Filtro em branco = todos."}
                    </MDTypography>
                )}

                {definicao.filtros.length === 0 && !definicao.descricaoFixa && (
                    <MDTypography variant="button" display="block" sx={{ color: textoSecundario }}>
                        Este relatório não tem filtros: é só gerar.
                    </MDTypography>
                )}

                {definicao.descricaoFixa && (
                    <MDBox display="flex" flexWrap="wrap" gap={0.75} mt={1.5}>
                        {definicao.descricaoFixa.map(texto => (
                            <Chip
                                key={texto}
                                size="small"
                                label={texto}
                                icon={<Icon sx={{ fontSize: "15px !important", color: `${textoSecundario} !important` }}>lock</Icon>}
                                sx={{ color: textoPrincipal, backgroundColor: darkMode ? "rgba(255,255,255,0.08)" : "#f0f2f5" }}
                            />
                        ))}
                    </MDBox>
                )}

                <MDBox
                    display="flex" alignItems="flex-start" gap={1} mt={2.5} p={1.25} borderRadius="8px"
                    sx={{ backgroundColor: darkMode ? ESCURO.painel : "#fafafa", border: darkMode ? `1px solid ${ESCURO.borda}` : "1px solid #eceff3" }}
                >
                    <Icon sx={{ color: darkMode ? "#81C784" : "#2E7D32", fontSize: "20px !important", mt: 0.1 }}>table_view</Icon>
                    <MDBox sx={{ flexGrow: 1 }}>
                        <MDTypography variant="caption" display="block" sx={{ color: textoSecundario }}>
                            {definicao.customizado && definicao.temTemplate
                                ? "A planilha sai no modelo próprio cadastrado para este relatório."
                                : "A planilha sai no padrão do sistema: título, filtros usados, valores formatados, totais e filtro do Excel em cada coluna. Para incluir uma tabela dinâmica, use \"Ver na tela\"."}
                        </MDTypography>
                        {!(definicao.customizado && definicao.temTemplate) && onAparencia && (
                            <MDButton variant="text" size="small" color="info" onClick={onAparencia} sx={{ px: 0, minHeight: 0, mt: 0.25, color: darkMode ? "#90CAF9 !important" : undefined }}>
                                <MDBox component="span" sx={{ width: 11, height: 11, borderRadius: "3px", backgroundColor: estilo?.cor || "#1F3864", mr: 0.75, display: "inline-block" }} />
                                Aparência da planilha
                            </MDButton>
                        )}
                    </MDBox>
                </MDBox>

                {erro && (
                    <Alert severity="warning" sx={{ mt: 2, fontSize: "0.85rem" }}>
                        {erro}
                    </Alert>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1, flexWrap: "wrap" }}>
                <MDButton
                    variant="text"
                    color={darkMode ? "white" : "secondary"}
                    onClick={onClose}
                    disabled={Boolean(acao)}
                >
                    Cancelar
                </MDButton>
                <MDButton
                    variant="outlined"
                    color="info"
                    onClick={() => executar("tela")}
                    disabled={Boolean(acao)}
                    sx={{ color: darkMode ? "#90CAF9 !important" : undefined, borderColor: darkMode ? "#90CAF9 !important" : undefined }}
                >
                    {acao === "tela" ? <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} /> : <Icon sx={{ mr: 0.75 }}>visibility</Icon>}
                    Ver na tela
                </MDButton>
                <MDButton variant="gradient" color="success" onClick={() => executar("planilha")} disabled={Boolean(acao)}>
                    {acao === "planilha" ? <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} /> : <Icon sx={{ mr: 0.75 }}>download</Icon>}
                    Baixar planilha
                </MDButton>
            </DialogActions>
        </Dialog>
    );
}

RelatorioProntoDialog.propTypes = {
    definicao: PropTypes.object,
    selecao: PropTypes.object,
    listas: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired,
    onVerNaTela: PropTypes.func.isRequired,
    onBaixar: PropTypes.func.isRequired,
    estilo: PropTypes.object,
    onAparencia: PropTypes.func,
};
